import { ClickAwayListener, Paper, Stack, Tooltip, Typography, styled } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { FC, ReactNode, useState, type JSX } from 'react';
import { useResponsiveText, useSmallScreen } from '../../hooks';
import { Nullable } from '../../types';
import { innerText } from '../../util';

interface DataRowProps {
  action?: JSX.Element;
  'data-testid'?: string;
  description?: Nullable<string>;
  label?: ReactNode;
  value: ReactNode;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  overflowX: 'hidden',
  padding: theme.spacing(1),
  width: '100%',
}));

export const DataRow: FC<DataRowProps> = ({ action, description, label, value, ...props }): JSX.Element => {
  const smallScreen = useSmallScreen();
  const [open, setOpen] = useState(false);
  const handleTooltipClose = (): void => setOpen(false);
  const handleTooltipOpen = (): void => setOpen(true);

  const labelLength = innerText(label).length;
  const captionStyles = useResponsiveText('0.75rem', labelLength, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 20,
  });

  const desktopLabel = <>
    {description
      ? <Tooltip title={description} placement={'right'}>
        <Typography
          color="text.secondary"
          variant="caption"
          lineHeight={1}
          style={{
            verticalAlign: 'top',
            fontSize: captionStyles.fontSize,
            wordBreak: captionStyles.wordBreak,
            whiteSpace: captionStyles.whiteSpace,
            overflow: captionStyles.overflow,
            textOverflow: captionStyles.textOverflow,
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      : <Typography
        color="text.secondary"
        variant="caption"
        lineHeight={1}
        style={{
          verticalAlign: 'top',
          fontSize: captionStyles.fontSize,
          wordBreak: captionStyles.wordBreak,
          whiteSpace: captionStyles.whiteSpace,
          overflow: captionStyles.overflow,
          textOverflow: captionStyles.textOverflow,
        }}
      >
        {label}
      </Typography>
    }
  </>;

  const mobileLabel = (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div onClick={handleTooltipOpen}>
        {description
          ? <Tooltip
            disableFocusListener
            disableHoverListener
            disableTouchListener
            onClose={handleTooltipClose}
            open={open}
            placement={'right'}
            slotProps={{
              popper: {
                disablePortal: true,
              },
            }}
            title={description}
          >
            <Typography
              color="text.secondary"
              variant="caption"
              lineHeight={1}
              style={{
                verticalAlign: 'top',
                fontSize: captionStyles.fontSize,
                wordBreak: captionStyles.wordBreak,
                whiteSpace: captionStyles.whiteSpace,
                overflow: captionStyles.overflow,
                textOverflow: captionStyles.textOverflow,
              }}
            >
              {label}
            </Typography>
          </Tooltip>
          : <Typography
            color="text.secondary"
            variant="caption"
            lineHeight={1}
            style={{
              verticalAlign: 'top',
              fontSize: captionStyles.fontSize,
              wordBreak: captionStyles.wordBreak,
              whiteSpace: captionStyles.whiteSpace,
              overflow: captionStyles.overflow,
              textOverflow: captionStyles.textOverflow,
            }}
          >
            {label}
          </Typography>
        }
      </div>
    </ClickAwayListener>
  );

  return <>
    <StyledPaper
      data-testid={props['data-testid']}
      elevation={0}
    >
      <Stack width="100%">
        <Box data-testid='row-label' sx={{ lineHeight: 1, mb: 0.5 }}>
          {smallScreen ? mobileLabel : desktopLabel}
        </Box>
        <Box data-testid='row-value' sx={{ lineHeight: 1.2, overflowX: 'auto', overflowY: 'hidden' }}>
          {value}
        </Box>
      </Stack>
      {action}
    </StyledPaper>
  </>;
};
